<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";

  .body {
    padding: 0 utils.in_rem(24px) utils.in_rem(24px) utils.in_rem(24px);
    gap: utils.in_rem(22px);
    display: flex;
    flex-direction: column;
    overflow: auto;
  }
  .checkbox {
    @include mixins.body3();
    display: flex;
    gap: utils.in_rem(8px);
    padding: utils.in_rem(12px);
  }

  .buttons {
    display: flex;
    justify-content: flex-end;
    padding: utils.in_rem(12px);
    border-top: solid 1px colors.get(interfaceSwGrey_10);
    gap: utils.in_rem(16px);
  }
</style>

<script>
  import Button from "@sourcewhale/shared/svelte-design-system/components/atoms/Button.svelte";
  import Checkbox from "@sourcewhale/shared/svelte-design-system/components/atoms/Checkbox/Checkbox.svelte";
  import Modal from "@sourcewhale/shared/svelte-design-system/components/molecules/Modal/Modal.svelte";
  import { showToast } from "@sourcewhale/shared/src/assets/js/src/general/utils";
  import { adminModify } from "../../../../api/admin";
  import { getAdminPanelContext } from "../context";
  import Notification from "../TableComponents/Notification.svelte";
  import Changes from "./Changes.svelte";

  const { rows, modulesStore } = getAdminPanelContext();

  export let show;

  const onclose = () => {
    show = false;
  };

  const change = (row, m) => {
    const operation = row.licenses[m.kind] ? "add" : "remove";
    const license = m.kind;
    const opText = row.licenses[m.kind] ? "Added" : "Removed";
    const description = `${m.title} License ${opText}`;

    return { operation, description, license };
  };

  const checkBoxTitle = (outreachRemovals, noLicensesUsers) => {
    const outreachRemovalTitle =
      "I understand that outreach and to dos for these users will be permanently deleted.";
    const noLicensesTitle = "I understand that these users will be deactivated";
    if (outreachRemovals.length > 0 && noLicensesUsers.length > 0)
      return `${noLicensesTitle} and ${outreachRemovalTitle}`;

    return outreachRemovals.length > 0
      ? outreachRemovalTitle
      : `${noLicensesTitle}.`;
  };

  $: outreachRemovals = $rows.flatMap((row) =>
    $modulesStore
      .filter((m) => m.kind === "outreach")
      .filter(
        (m) =>
          (row.originalLicenses || {})[m.kind] && !(row.licenses || {})[m.kind]
      )
      .map((m) => ({ ...row, ...change(row, m) }))
  );

  // Count users who had any licenses and now have none
  $: noLicensesUsers = $rows
    .filter((row) => {
      const hadLicenses = Object.values(row.originalLicenses).some(Boolean);
      const hasNoLicenses = !Object.values(row.licenses).some(Boolean);
      return hadLicenses && hasNoLicenses;
    })
    .map((row) => ({
      ...row,
      description: "All licenses removed",
      secondaryDescription: "User will be deactivated",
    }));

  $: safeChanges = $rows
    .filter(
      (row) =>
        !noLicensesUsers.includes(row.id) && !outreachRemovals.includes(row.id)
    )
    .flatMap((row) =>
      $modulesStore
        .filter(
          (m) =>
            (row.originalLicenses || {})[m.kind] !==
            (row.licenses || {})[m.kind]
        )
        .filter((m) => m.kind !== "outreach" || row.licenses?.outreach)
        .map((m) => ({
          ...row,
          ...change(row, m),
          visible: !noLicensesUsers.some((u) => u.userId === row.userId),
        }))
    );

  let clientHeight = 0;
  let cbHeight = 0;

  $: if (outreachRemovals.length === 0) cbHeight = 0;
  let checked = false;
  $: disabled = outreachRemovals.length && !checked;
  $: cls = disabled ? "disabled" : "";

  const saveChanges = async () => {
    const changes = outreachRemovals
      .concat(safeChanges)
      .map((x) =>
        Object.fromEntries(
          Object.entries(x).filter(([k]) =>
            ["userId", "teamId", "license", "operation"].includes(k)
          )
        )
      );
    await adminModify({
      kind: "assign-licenses",
      data: { changes },
    });

    show = false;

    showToast({
      title: "Success",
      type: "success",
      subtitle: "License changes saved",
    });
  };
</script>

{#if show}
  <Modal size="medium" {onclose}>
    <h2 slot="header">Review and Save License Changes</h2>
    <div
      class="body"
      style="height:calc(100% - {clientHeight}px - {cbHeight}px)"
    >
      {#if outreachRemovals.length > 0 || noLicensesUsers.length > 0}
        <div>
          <Notification
            outreachCount={outreachRemovals.length}
            noLicensesCount={noLicensesUsers.length}
            showAffectedUsers={false}
          />
        </div>

        {#if outreachRemovals.length > 0}
          <Changes changes={outreachRemovals} />
        {/if}

        {#if noLicensesUsers.length > 0}
          <Changes changes={noLicensesUsers} />
        {/if}

        {#if safeChanges.filter((c) => c.visible).length}
          <h4>Other changes</h4>
        {/if}
      {/if}

      <Changes changes={safeChanges.filter((c) => c.visible)} />
    </div>
    {#if outreachRemovals.length > 0 || noLicensesUsers.length > 0}
      <div class="checkbox" bind:clientHeight={cbHeight}>
        <Checkbox bind:checked />
        {checkBoxTitle(outreachRemovals, noLicensesUsers)}
      </div>
    {/if}

    <div class="buttons" bind:clientHeight>
      <Button kind="tertiary" onclick={onclose}>Cancel</Button>
      <Button kind="primary" class={cls} onclick={saveChanges} {disabled}
        >Confirm & Save</Button
      >
    </div>
  </Modal>
{/if}
